import { useState, useEffect, useCallback } from 'react'
import { getOrCreateStoredFingerprint } from '@/shared/lib/deviceFingerprint'
import { STATIC_TERMS, STATIC_PRIVACY } from '@/shared/legal/staticLegal'
import type { LegalDocument } from '@/shared/components/EmbeddedLegalConsentModal'

interface LegalConsentState {
  hasAcceptedLatest: boolean
  isLoading: boolean
  error: string | null
  documents: {
    terms: LegalDocument | null
    privacy: LegalDocument | null
  }
  acceptedVersions: {
    terms: number | null
    privacy: number | null
  }
  showModal: boolean
}

interface LegalConsentActions {
  checkConsent: () => Promise<void>
  acceptConsent: () => Promise<void>
  refresh: () => Promise<void>
  closeModal: () => void
  clearConsent: () => void // For testing purposes
}

const STORAGE_KEY = 'travel_warehouse_legal_consent'
const CHECK_INTERVAL = 24 * 60 * 60 * 1000 // 24 hours

// For debugging localStorage
const DEBUG_CONSENT = false

const debugLog = (message: string, data?: any) => {
  if (DEBUG_CONSENT) {
    console.log(`[LegalConsent] ${message}`, data || '')
  }
}

export function useLegalConsent(): LegalConsentState & LegalConsentActions {
  const [state, setState] = useState<LegalConsentState>({
    hasAcceptedLatest: false,
    isLoading: true,
    error: null,
    documents: {
      terms: null,
      privacy: null
    },
    acceptedVersions: {
      terms: null,
      privacy: null
    },
    showModal: false
  })

  // Get stored consent data from localStorage on page load
  const getStoredConsent = useCallback(() => {
    debugLog('Checking localStorage for consent data')
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      debugLog('Raw localStorage data:', stored)
      
      if (stored) {
        const parsed = JSON.parse(stored)
        const consent = {
          termsVersion: parsed.termsVersion || null,
          privacyVersion: parsed.privacyVersion || null,
          timestamp: parsed.timestamp || null
        }
        debugLog('Parsed consent data:', consent)
        return consent
      } else {
        debugLog('No consent data found in localStorage')
      }
    } catch (error) {
      console.error('Error reading stored consent:', error)
      // Clear corrupted data
      localStorage.removeItem(STORAGE_KEY)
    }
    
    const emptyConsent = {
      termsVersion: null,
      privacyVersion: null,
      timestamp: null
    }
    debugLog('Returning empty consent data')
    return emptyConsent
  }, [])

  // Store consent data with validation
  const storeConsent = useCallback((termsVersion: number, privacyVersion: number) => {
    debugLog('Storing consent data', { termsVersion, privacyVersion })
    
    try {
      // Validate version numbers
      if (!termsVersion || !privacyVersion || termsVersion < 1 || privacyVersion < 1) {
        throw new Error('Invalid version numbers provided')
      }
      
      const consentData = {
        termsVersion,
        privacyVersion,
        timestamp: Date.now(),
        acceptedAt: new Date().toISOString(),
        userAgent: navigator.userAgent.substring(0, 200) // Truncate for storage limits
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData))
      debugLog('Successfully stored consent data')
    } catch (error) {
      console.error('Error storing consent:', error)
      throw error // Re-throw to handle in calling function
    }
  }, [])

  // Check if we should skip consent check (to avoid spam)
  const shouldSkipCheck = useCallback(() => {
    const stored = getStoredConsent()
    if (!stored.timestamp) return false
    
    // If this looks like a new device, do NOT skip the check
    const { isNew } = getOrCreateStoredFingerprint()
    if (isNew) return false

    const timeSinceCheck = Date.now() - stored.timestamp
    return timeSinceCheck < CHECK_INTERVAL
  }, [getStoredConsent])

  // Provide legal documents statically (no API needed)
  const fetchDocuments = useCallback(async () => {
    return {
      terms: STATIC_TERMS,
      privacy: STATIC_PRIVACY,
    }
  }, [])

  // Check consent status on page load
  const checkConsent = useCallback(async () => {
    debugLog('Starting consent check on page load')
    
    // Get stored consent data first
    const stored = getStoredConsent()
    debugLog('Stored consent versions:', {
      terms: stored.termsVersion,
      privacy: stored.privacyVersion,
      timestamp: stored.timestamp ? new Date(stored.timestamp).toISOString() : null
    })
    
    // Skip if we've checked recently and have accepted versions
    if (shouldSkipCheck() && stored.termsVersion && stored.privacyVersion) {
      debugLog('Skipping check - recently accepted and within check interval')
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        hasAcceptedLatest: true,
        acceptedVersions: {
          terms: stored.termsVersion,
          privacy: stored.privacyVersion
        }
      }))
      return
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))
    debugLog('Fetching latest document versions from API')

    try {
      // Fetch current documents
      const documents = await fetchDocuments()
      debugLog('Fetched document versions:', {
        terms: documents.terms.version,
        privacy: documents.privacy.version
      })

      // Compare stored versions with current versions
      const hasAcceptedLatest = 
        stored.termsVersion === documents.terms.version &&
        stored.privacyVersion === documents.privacy.version
      
      debugLog('Version comparison result:', {
        hasAcceptedLatest,
        storedTerms: stored.termsVersion,
        currentTerms: documents.terms.version,
        storedPrivacy: stored.privacyVersion,
        currentPrivacy: documents.privacy.version
      })

      const { isNew } = getOrCreateStoredFingerprint()
      setState(prev => ({
        ...prev,
        documents,
        acceptedVersions: {
          terms: stored.termsVersion,
          privacy: stored.privacyVersion
        },
        hasAcceptedLatest,
        // Always show modal for brand-new devices, or when versions changed
        showModal: isNew || !hasAcceptedLatest,
        isLoading: false
      }))
      
      debugLog(hasAcceptedLatest ? 'User has accepted latest versions' : 'Modal will be shown - consent required')
    } catch (error) {
      console.error('Failed to check consent:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to check consent',
        isLoading: false
      }))
    }
  }, [fetchDocuments, getStoredConsent, shouldSkipCheck])

  // Accept consent
  const acceptConsent = useCallback(async () => {
    if (!state.documents.terms || !state.documents.privacy) {
      throw new Error('Documents not loaded')
    }

    try {
      // Store consent locally
      storeConsent(
        state.documents.terms.version,
        state.documents.privacy.version
      )

      // Update state
      setState(prev => ({
        ...prev,
        hasAcceptedLatest: true,
        showModal: false,
        acceptedVersions: {
          terms: prev.documents.terms?.version || null,
          privacy: prev.documents.privacy?.version || null
        }
      }))

      // Optional: Send consent to backend for analytics/compliance
      // No backend logging when using static content
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to save consent'
      }))
      throw error
    }
  }, [state.documents, storeConsent])

  // Close modal (for testing/debugging)
  const closeModal = useCallback(() => {
    setState(prev => ({ ...prev, showModal: false }))
  }, [])
  
  // Clear stored consent (for testing/debugging)
  const clearConsent = useCallback(() => {
    debugLog('Clearing stored consent data')
    localStorage.removeItem(STORAGE_KEY)
    setState(prev => ({
      ...prev,
      hasAcceptedLatest: false,
      acceptedVersions: { terms: null, privacy: null },
      showModal: true
    }))
  }, [])

  // Refresh documents
  const refresh = useCallback(async () => {
    await checkConsent()
  }, [checkConsent])

  // Initialize on mount - check localStorage immediately on page load
  useEffect(() => {
    debugLog('Component mounted - initializing legal consent check')
    
    // Immediate localStorage check for better UX
    const stored = getStoredConsent()
    if (stored.termsVersion && stored.privacyVersion) {
      debugLog('Found existing consent in localStorage, checking if still valid')
    } else {
      debugLog('No existing consent found, will need to show modal')
    }
    
    // Perform full consent check
    // Ensure device fingerprint is established immediately
    getOrCreateStoredFingerprint()
    checkConsent()
  }, [checkConsent, getStoredConsent])

  return {
    ...state,
    checkConsent,
    acceptConsent,
    refresh,
    closeModal,
    clearConsent
  }
}